import { html, LitElement, type HTMLTemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";

import type { Constructor } from "@/internals/util-types";

import type AtlasHelperText from "@/components/form/atlas-helper-text/atlas-helper-text";

import "@/components/form/atlas-helper-text/atlas-helper-text";

type RenderHelperTextProps = {
    isInputHelper?: boolean;
    isToggleHelper?: boolean;
    white?: boolean;
};

export type WithHelperTextProps = {
    "helper-text": string;
};

export declare class WithHelperTextInterface {
    helperText: string;

    hasHelperText(): boolean;

    shouldShowHelperText(): boolean;

    renderHelperText(attributes?: RenderHelperTextProps): HTMLTemplateResult;

    renderHelperTextSkeleton(): HTMLTemplateResult;
}

export const WithHelperTextMixin = <T extends Constructor<LitElement>>(superClass: T) => {
    /**
     * @dependency atlas-helper-text
     */
    class WithHelperTextClass extends superClass implements WithHelperTextInterface {
        /** Texto de ajuda */
        @property({ type: String, attribute: "helper-text" }) helperText: string = "";

        @state() private _hasSlottedHelperText: boolean = false;

        private _helperTextProps: Required<RenderHelperTextProps> = {
            white: false,
            isInputHelper: false,
            isToggleHelper: false
        };

        /** @internal */
        public hasHelperText() {
            return !!this.helperText || this._hasSlottedHelperText;
        }

        /** @internal */
        public shouldShowHelperText() {
            return this.hasHelperText();
        }

        /** @internal */
        public renderHelperText(props: RenderHelperTextProps = {}) {
            this._helperTextProps = {
                ...this._helperTextProps,
                ...props
            };

            return when(
                !!this.helperText,
                () => html`
                    <atlas-helper-text
                        id=${this.id}
                        ?white=${this._helperTextProps.white}
                        ?hidden=${!this.shouldShowHelperText()}
                        ?is-input-helper=${this._helperTextProps.isInputHelper}
                        ?is-toggle-helper=${this._helperTextProps.isToggleHelper}
                    >
                        ${this.helperText}
                    </atlas-helper-text>
                `,
                () => html`
                    <slot
                        name="helper-text"
                        ?hidden=${!this.shouldShowHelperText()}
                        @slotchange=${() => this.onHelperTextSlotChange()}
                    ></slot>
                `
            );
        }

        /** @internal */
        public renderHelperTextSkeleton() {
            return when(
                this.hasHelperText(),
                () => html`<div class="helper-text skeleton" ?hidden=${!this.shouldShowHelperText()}></div>`
            );
        }

        private applyHelperTextAttributes(element: AtlasHelperText) {
            element.toggleAttribute("white", this._helperTextProps.white);
            element.toggleAttribute("is-input-helper", this._helperTextProps.isInputHelper);
            element.toggleAttribute("is-toggle-helper", this._helperTextProps.isToggleHelper);

            if (!element.hasAttribute("id")) element.setAttribute("id", this.id);
        }

        private onHelperTextSlotChange() {
            const helperTextSlot = this.shadowRoot.querySelector("slot[name='helper-text']") as HTMLSlotElement;

            this.processSlottedHelperTextElements(helperTextSlot);
        }

        private processSlottedHelperTextElements(slot: HTMLSlotElement) {
            const assignedElements = slot?.assignedElements() || [];

            this._hasSlottedHelperText = assignedElements.length > 0;

            if (!this._hasSlottedHelperText) return;

            const helperTextElements: AtlasHelperText[] = [];
            const slotElements: HTMLSlotElement[] = [];
            const otherElements: Element[] = [];

            assignedElements.forEach((element) => {
                if (element.tagName === "ATLAS-HELPER-TEXT") {
                    helperTextElements.push(element as AtlasHelperText);
                } else if (element.tagName === "SLOT") {
                    slotElements.push(element as HTMLSlotElement);
                } else {
                    otherElements.push(element);
                }
            });

            helperTextElements.forEach((element) => {
                this.applyHelperTextAttributes(element);
            });

            slotElements.forEach((slotElement) => {
                this.processSlottedHelperTextElements(slotElement);
            });

            if (otherElements.length > 0) {
                this.wrapElementsInHelperText(otherElements);
            }
        }

        private wrapElementsInHelperText(elements: Element[]) {
            const wrapper = document.createElement("atlas-helper-text");

            this.applyHelperTextAttributes(wrapper);

            wrapper.setAttribute("slot", "helper-text");

            elements[0].before(wrapper);

            elements.forEach((element) => {
                element.removeAttribute("slot");

                wrapper.appendChild(element);
            });
        }
    }

    return WithHelperTextClass as Constructor<WithHelperTextInterface> & T;
};
